import eventBus from '../../../../../dashboards/event-bus';
import { filterToQueryString, queryStringToFilter } from '../url-filters-util';
import { baseFilterFieldModel } from '../filter-field-model';
import SofyAbortController from '/src/common/sofy-abort-controller/sofy-abort-controller';

let distinctFieldsAbortController;
export const actions = {
  /**
   * Method to fetch distinct fields for filter
   */
  async getFilterFields({ commit, state }) {
    try {
      commit('setHasLoadedDistinctFields', false);
      /**
       * Combine main dashboard's widget brRefs + all cockpits active tabs widget brRefs + object template's widgets' brRefs
       */
      const cockpitAndObjTemplateBrRefs = [
        ...Object.values(state.brRefsByCockpitUUID),
        ...Object.values(state.brRefsByObjectTemplateUUID)
      ].reduce((combinedRefs, brRefs) => combinedRefs.concat(brRefs || []), []);

      let allBrRefsOnDashboard = [...new Set(state.dashboardBrRefs.concat(cockpitAndObjTemplateBrRefs))];

      // first try to read from cache
      if (state.distinctFieldsCache[allBrRefsOnDashboard]) {
        commit('setDistinctFields', state.distinctFieldsCache[allBrRefsOnDashboard]);
        commit('setLoadingDistinctFields', false);
        commit('setHasLoadedDistinctFields', true);
      } else {
        // Cancel the previous apis.
        if (distinctFieldsAbortController) {
          distinctFieldsAbortController.abort();
        }
        commit('setLoadingDistinctFields', true);
        distinctFieldsAbortController = new SofyAbortController();
        let distinctFields = await this.$axios.$get(`/api/data/distinct-fields`, {
          params: { refs: allBrRefsOnDashboard },
          signal: distinctFieldsAbortController.signal
        });
        commit('setDistinctFields', distinctFields);
        commit('setLoadingDistinctFields', false);
        commit('setHasLoadedDistinctFields', true);

        // Add to cache
        commit('setDistinctFieldsCache', { brRefs: allBrRefsOnDashboard, result: distinctFields });
      }
    } catch (error) {
      if (error?.message !== 'canceled') {
        commit('setLoadingDistinctFields', false);
        commit('setHasLoadedDistinctFields', true);
        this.$toast.error(this.$errorFromResponse('common.top_filter_bar.fetch_distinct_field_error', error));
      }
    }
  },

  /**
   * Applies page filter for single column
   * Input Example
   *
   * [{
   *    'Name' : { values: ['test'], operator : 'in'}
   * }]
   *
   * OR
   *
   * [{
   *    'Name' : { values: ['test'], operator : 'in'}
   * },
   * {
   *    'ID' : { values: [1], operator : 'in'}
   * }]
   *
   */
  applyPageFilters({ commit, state }, selectionObjects) {
    /**
     * STEP - 1 Update Filter Model for all columns in selection object
     * Updating filter model for the filter field so that can be reflected in UI
     */
    const newModel = selectionObjects.reduce(
      (model, filter) => ({
        ...model,
        [filter.columnName]: baseFilterFieldModel(null, filter.selectionObject)
      }),
      {}
    );
    commit('updateFilterValueModel', newModel);

    /**
     * STEP 2 - Update appliedPageFilter for all columns in selectionObject and emit event to refresh widgets
     * - If values, min- max value, start-end date are null which means the filter is cleared so remove that applied filter
     * - If the filter is existing filter and value is updated update the value in that existing applied filter
     * - If not existing add it as a new applied filter
     */
    selectionObjects.forEach(({ columnName, selectionObject }) => {
      const existingFilter = state.appliedPageFilters.find(
        (appliedPageFilter) => appliedPageFilter.name === columnName
      );
      if (
        (!selectionObject.values || selectionObject.values.length === 0) &&
        typeof selectionObject.minValue !== 'number' &&
        typeof selectionObject.maxValue !== 'number' &&
        !selectionObject.startDate &&
        !selectionObject.endDate &&
        !selectionObject.includeNulls
      ) {
        commit('removeAppliedPageFiltersForColumns', [columnName]);
      } else if (existingFilter) {
        commit('updateExistingAppliedPageFilter', { columnName, selectionObject });
      } else {
        commit('addAppliedPageFilter', { columnName, selectionObject });
        commit('setUndoFilters', {
          operation: 'Apply',
          filter: state.appliedPageFilters.find((filter) => filter.name === columnName)
        });
      }
    });

    /**
     * STEP 3 - Emit event to refresh related widgets
     */
    eventBus.$emit('refresh-filter-affected-widgets');

    // STEP 4 - Push filter to URL
    filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
  },

  /**
   * Removes filters for given columns (Filter Bar Clear All)
   */
  clearPageFilterForColumns({ state, commit }, { columns, isUndoFilter }) {
    if (isUndoFilter) {
      commit('setUndoFilters', {
        operation: 'Remove',
        filter: state.appliedPageFilters.find((filter) => filter.name === columns[0])
      });
    }

    // STEP 1 - Reset filter model for given columns
    commit('resetFilterModelForColumns', columns);

    // STEP 2 - Remove applied page filters for given columns
    commit('removeAppliedPageFiltersForColumns', columns);

    // STEP 3 - Emit event to refresh widgets
    eventBus.$emit('refresh-filter-affected-widgets');

    // STEP 4 - Push filter to URL
    filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
  },

  /**
   * Clears All applied page filters
   * @param {Object} context context
   */
  clearAllPageFilters({ state, commit }, refreshWidgetFlag = true) {
    // STEP 1 - Extract the mandatory params to be kept
    let columns = state.appliedPageFilters.map((filter) => filter.name);
    const mandatoryColumns = state.preloadFilters.filter((field) => field.isMandatoryParam).map((field) => field.field);
    if (state.preloadFilters.length > 0) {
      columns = columns.filter((column) => !mandatoryColumns.includes(column));
    }

    const filterToRemove = state.appliedPageFilters.filter((appliedFilter) => columns.includes(appliedFilter.name));
    for (let filter of filterToRemove) {
      commit('setUndoFilters', { operation: 'Remove', filter: filter });
    }

    // STEP 2 - Reset filter model completely with default value for all cols
    commit('resetFilterModelForColumns', columns);

    // STEP 3 - Remove all applied page filters except mandatory param
    commit('removeAppliedPageFiltersForColumns', columns);

    // STEP 4 - Emit event to refresh widgets
    refreshWidgetFlag && eventBus.$emit('refresh-filter-affected-widgets');

    // STEP 5 - Push filter to URL
    filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
  },

  /**
   * Reloads the filter based available in url
   * @param {Object} context context
   * @param {Object} filterValueModel object of all the fields with the selected values
   */
  reloadFilterFromUrl({ state, commit }, Url) {
    //STEP1 - to extract the filters from the URL
    const filterValueModel = queryStringToFilter(Url, state.preloadFilters);
    //STEP 2 - set the acWidgetFilters if available.
    if (filterValueModel['ac-filters']) {
      commit('updateAcWidgetFilters', filterValueModel['ac-filters']);
      delete filterValueModel['ac-filters'];
    }

    //STEP 3 - set the filterValueModel
    commit('updateFilterValueModel', filterValueModel);

    //STEP 4 - Now we will extract each object field from the filterValueModel and add that to the applied filter in state.
    for (const [key, value] of Object.entries(filterValueModel)) {
      // Add filters only if key not available already.Since this method is currently called from created of dashboard-display,
      // there is no need found for updating the values of already existing filters(key) as of now.
      if (!state.appliedPageFilters.some((filter) => filter['name'] === key)) {
        commit('addAppliedPageFilter', {
          columnName: key,
          selectionObject: {
            ...value,
            isNumeric: value.fieldType === 'int' ? 'Yes' : undefined,
            type: value.fieldType === 'date' ? 'date' : undefined
          }
        });
      }
    }
  },
  /**
   * Applies access-control widget filter on cockpits.
   * Input Example
   *
   * { 'ac-filters[userID]' :'id1' }
   *
   */
  applyAcWidgetFilters({ commit, state }, acWidgetFilterObject) {
    /**
     * STEP - 1
     * Updating access-control widget filter model so that can be reflected in UI
     */
    commit('updateAcWidgetFilters', acWidgetFilterObject);

    // STEP 2 - Push filter to URL
    filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
  },

  /**
   * Removes specified access-control widget filter on cockpits.
   * @param {Object} context context
   * @param {String[]} filters filters which are to be removed from ac widget filters
   */
  removeSpecifiedAcWidgetFilters({ commit, state }, filters) {
    /**
     * STEP - 1
     * Removing filter from access-control widget filter model
     */
    commit('removeSpecificAcWidgetFilters', filters);

    // STEP 2 - Push filter to URL
    filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
  },

  /**
   * Method to undo the applied filter on the dashboard
   */
  undoPageFilters({ commit, state }) {
    if (state.undoFilters.length > 0) {
      const removeEntryFromUndoFilter = state.undoFilters.pop();
      // STEP 1 - add to the redo filter
      commit('setRedoFilters', removeEntryFromUndoFilter);

      if (removeEntryFromUndoFilter.operation === 'Apply' && state.appliedPageFilters.length > 0) {
        const filterToRemove = state.appliedPageFilters.pop();

        // STEP 2 - add the filter to the undoFilter state
        commit('setUndoFilters', { operation: 'Apply', filter: filterToRemove });

        // STEP 3 - Reset filter model for given columns
        commit('resetFilterModelForColumns', [filterToRemove.name]);

        // STEP 4 - Remove applied page filters for given columns
        commit('removeAppliedPageFiltersForColumns', [filterToRemove.name]);
      } else {
        // STEP 2 we need to populate the values in the filter model
        commit('updateFilterValueModel', {
          [removeEntryFromUndoFilter.filter.name]: baseFilterFieldModel(null, removeEntryFromUndoFilter.filter)
        });

        // STEP 3 push to the applied page filter array
        commit('pushToAppliedPageFilters', removeEntryFromUndoFilter.filter);
      }

      // STEP 4/5 - Emit event to refresh widgets
      eventBus.$emit('refresh-filter-affected-widgets');

      // STEP 5/6 - Remove filter from URL
      filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
    }
  },

  /**
   * Method to redo the filter which are undo from the dashboard
   */
  redoPageFilters({ commit, state }) {
    if (state.redoFilters.length > 0) {
      const filterToAdd = state.redoFilters.pop();
      // STEP 1 add this to the undo filter array
      commit('setUndoFilters', filterToAdd);

      if (filterToAdd.operation === 'Remove') {
        // STEP 2 Reset filter model for given columns
        commit('resetFilterModelForColumns', [filterToAdd.filter.name]);

        // STEP 3 Remove applied page filters for given columns
        commit('removeAppliedPageFiltersForColumns', [filterToAdd.filter.name]);
      } else {
        // STEP 2 we need to populate the values in the filter model
        commit('updateFilterValueModel', { [filterToAdd.filter.name]: baseFilterFieldModel(null, filterToAdd.filter) });

        const isExist = state.appliedPageFilters.find((filter) => filter.name === filterToAdd.filter.name);
        if (!isExist) {
          // STEP 3 push to the applied page filter array
          commit('pushToAppliedPageFilters', filterToAdd.filter);
        }
      }

      // STEP 4 Emit event to refresh widgets
      eventBus.$emit('refresh-filter-affected-widgets');

      // STEP 5 Push filter to URL
      filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
    }
  },

  applyBookmarkFilters({ commit, state }, filterJson) {
    let columns = state.appliedPageFilters.map((filter) => filter.name);

    const mandatoryColumns = state.preloadFilters.filter((field) => field.isMandatoryParam).map((field) => field.field);
    if (state.preloadFilters.length > 0) {
      columns = columns.filter((column) => !mandatoryColumns.includes(column));
    }

    const columnsToRemove = state.appliedPageFilters
      .filter((appliedFilter) => columns.includes(appliedFilter.name))
      .map((filter) => filter.name);

    //STEP 1 We will reset all the values in the filter value model state
    commit('resetFilterModelForColumns', columnsToRemove);

    //STEP 2 We will remove all the applied page filters
    commit('removeAppliedPageFiltersForColumns', columnsToRemove);

    try {
      filterJson = JSON.parse(filterJson);

      for (let filter of filterJson) {
        // STEP 3 we need to populate the values in the filter model
        commit('updateFilterValueModel', { [filter.name]: baseFilterFieldModel(null, filter) });

        // STEP 4 push to the applied page filter array
        if (mandatoryColumns.includes(filter.name)) {
          commit('updateExistingAppliedPageFilter', {
            columnName: filter.name,
            selectionObject: { ...filter, isNumeric: filter.isNumericType }
          });
        } else {
          commit('pushToAppliedPageFilters', filter);
        }
      }

      // STEP 4 Emit event to refresh widgets
      eventBus.$emit('refresh-filter-affected-widgets');

      // STEP 6 Push filter to URL
      filterToQueryString(state.appliedPageFilters, state.acWidgetFilters);
    } catch (error) {
      this.$toast.error(this.$errorFromResponse('common.bookmark_filters.apply_bookmark_filter_error', error));
    }
  }
};
